import { clsx } from 'clsx';
import { Card, CardContent } from '../../Card/Card';
import { Badge } from '../../Badge/Badge';
import { useMasterDocumentFormatters } from '../formatters';
import { useFinancialInstrumentTranslation } from './i18n';
import { defaultFormatAmount, defaultFormatDate } from './format';
import type { FinancialInstrumentDto } from './types';

export interface FinancialInstrumentFactBoxProps {
  /** IF activo del documento, o `null` si no hay. */
  instrument: FinancialInstrumentDto | null;
  /**
   * `true` si la condición de pago exige IF (`requires_financial_instrument`).
   * Cuando es `true` y no hay IF, muestra el placeholder "Pendiente de captura"
   * (105-17.03 — AUDIT-NOTE: la épica pedía ocultar; confirmar con PO). Cuando es
   * `false` y no hay IF, el widget no renderiza nada (zero footprint).
   */
  requiresInstrument?: boolean;
  /** Formateador de montos. @default `defaultFormatAmount` (idioma activo, 2 decimales). */
  formatAmount?: (value: number) => string;
  /** Formateador de fechas YYYY-MM-DD. @default formato medio del idioma activo. */
  formatDate?: (isoDate: string) => string;
  /** Clases CSS adicionales. */
  className?: string;
}

const DASH = '—';

/**
 * Widget de resumen del Instrumento Financiero para el FactBox (105-17.03).
 *
 * Read-only. Con IF activo muestra `{tipo} · N° {número} · {monto} · Vence
 * {fecha}`. Sin IF y con `requiresInstrument` muestra "Pendiente de captura". Sin
 * IF y sin requerimiento, no renderiza nada (zero footprint).
 */
export function FinancialInstrumentFactBox({
  instrument,
  requiresInstrument = false,
  formatAmount: formatAmountProp,
  formatDate: formatDateProp,
  className,
}: FinancialInstrumentFactBoxProps) {
  const { t } = useFinancialInstrumentTranslation();
  const { locale } = useMasterDocumentFormatters();
  const formatAmount = formatAmountProp ?? ((value: number) => defaultFormatAmount(value, locale));
  const formatDate = formatDateProp ?? ((iso: string) => defaultFormatDate(iso, locale));

  // Zero footprint: sin IF y sin requerimiento → nada.
  if (!instrument && !requiresInstrument) return null;

  const title = t('financial_instrument.factbox.title');

  if (!instrument) {
    return (
      <Card className={clsx('w-full', className)}>
        <CardContent className="flex flex-col gap-1.5 py-3">
          <span className="text-xs font-bold text-content-secondary dark:text-dark-content-secondary uppercase tracking-wide">
            {title}
          </span>
          <Badge color="amber" label={t('financial_instrument.factbox.pending')} />
        </CardContent>
      </Card>
    );
  }

  const typeName = instrument.instrumentTypeName || DASH;

  return (
    <Card className={clsx('w-full', className)}>
      <CardContent className="flex flex-col gap-1.5 py-3">
        <span className="text-xs font-bold text-content-secondary dark:text-dark-content-secondary uppercase tracking-wide">
          {title}
        </span>
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-content-primary dark:text-dark-content-primary">
          <span className="font-medium">{typeName}</span>
          <span className="text-content-tertiary dark:text-dark-content-tertiary">·</span>
          <span>{t('financial_instrument.factbox.number', { number: instrument.instrumentNumber })}</span>
          <span className="text-content-tertiary dark:text-dark-content-tertiary">·</span>
          <span className="tabular-nums font-medium">{formatAmount(instrument.amount)}</span>
          <span className="text-content-tertiary dark:text-dark-content-tertiary">·</span>
          <span className="text-content-secondary dark:text-dark-content-secondary">
            {t('financial_instrument.factbox.due', { date: formatDate(instrument.dueDate) })}
          </span>
        </div>
      </CardContent>
    </Card>
  );
}
